import React, { useEffect, useState } from 'react';
import { SubscriptionOverlay } from '../../../components/SubscriptionOverlay';
import { logger } from '../../../utils/logger';
import { getCurriculumPricing } from '../../curriculum/api/getCurriculumPricing';
import { CurriculumPricing } from '../../curriculum/models/curriculumPricing';
import { CurriculumData, findCurriculumForItemNumber } from '../utils/findCurriculumForItemNumber';
import formatDate from '../utils/formatDate';

type Instruction = {
  title: string;
  content: React.ReactNode;
};

const getInstructions = (oldPlan?: CurriculumData, newPlan?: CurriculumData, renewalDate?: string): Instruction[] => {
  if (!oldPlan || !newPlan || !renewalDate) return [];
  const sameBrandAndAgeGroup = oldPlan?.brand?.code === newPlan?.brand?.code;
  const newTitle = newPlan?.ageGroup ?? '';
  const formattedDate = formatDate(renewalDate);

  if (sameBrandAndAgeGroup) {
    if (oldPlan?.subscription?.size > newPlan?.subscription?.size) {
      return [
        {
          title: 'Prepare',
          content: (
            <p>
              Everyone will keep access to your current curriculum until {formattedDate}. After that date, you will need
              to add people so that they have access to your new curriculum.
            </p>
          ),
        },
        {
          title: 'Distribute',
          content: (
            <p>
              You need to add your leaders on {formattedDate} to give them access to the new content. This step is
              critical to ensure no interruptions in access. Add your leaders to a collection from the "people" page.
            </p>
          ),
        },
      ];
    }
    return [
      {
        title: 'Prepare',
        content: (
          <p>
            Everyone will keep access to your current curriculum until {formattedDate}. At that time, anyone who
            previously had access to age groups that are not included in {newTitle} will lose access to sessions.
          </p>
        ),
      },
      {
        title: 'Distribute',
        content: (
          <p>
            You need to add your leaders on {formattedDate} to give them access to the new content. This step is
            critical to ensure no interruptions in access. Add your leaders to a collection from the "people" page.
          </p>
        ),
      },
    ];
  }
  return [
    {
      title: 'Prepare',
      content: (
        <p>
          Everyone's access to your current curriculum will end on {formattedDate}. On that same date, access to your
          selection above will begin.
        </p>
      ),
    },
    {
      title: 'Distribute',
      content: (
        <p>
          You need to add your leaders on {formattedDate} to give them access to the new content. This step is critical
          to ensure no interruptions in access. Add your leaders to a collection from the "people" page.
        </p>
      ),
    },
  ];
};

export default function SwitchCurriculumConfirmationOverlay({
  open,
  dismiss,
  oldPlanItemNumber,
  newPlanItemNumber,
  renewalDate,
}: {
  open: boolean;
  dismiss: () => void;
  oldPlanItemNumber: string;
  newPlanItemNumber: string;
  renewalDate: string;
}) {
  const [data, setData] = useState<{
    newPlan?: CurriculumData;
    oldPlan?: CurriculumData;
    isLoading: boolean;
    isError?: boolean;
  }>({
    isLoading: true,
  });
  const { newPlan, oldPlan, isLoading, isError } = data;

  useEffect(() => {
    setData({ isLoading: true });

    if (oldPlanItemNumber && newPlanItemNumber && open) {
      getCurriculumPricing()
        .then((curriculumPricing: CurriculumPricing[]) => {
          const oldPlan = findCurriculumForItemNumber(curriculumPricing ?? [], oldPlanItemNumber);
          const newPlan = findCurriculumForItemNumber(curriculumPricing ?? [], newPlanItemNumber);

          if (!oldPlan || !newPlan) {
            throw new Error('Plan not found in pricing data');
          }

          setData({ oldPlan, newPlan, isLoading: false });
        })
        .catch(error => {
          logger.error(error);
          setData({ isLoading: false, isError: true });
        });
    }
  }, [oldPlanItemNumber, newPlanItemNumber, open]);

  if (!open) return null;

  return (
    <SubscriptionOverlay
      title="New Subscription"
      heading="Your subscription has been switched to the following curriculum:"
      subscriptions={
        newPlan
          ? [
              {
                name: newPlan.brand?.name ?? '',
                subtitle: `${newPlan.ageGroup ?? ''} ${
                  newPlan?.subscription?.size ? `(${newPlan.subscription.size})` : ''
                }`,
                brandCode: newPlan.brand?.code,
                additionalDetail: `Begins ${formatDate(renewalDate)}`,
              },
            ]
          : []
      }
      instructions={getInstructions(oldPlan, newPlan, renewalDate)}
      dismissHandler={dismiss}
      isLoading={isLoading}
      isError={isError}
    />
  );
}
